import { classNames } from 'primereact/utils';
import { useTranslation } from 'react-i18next';

import { EmissionValueWithSuffix } from '../../../../../../../../../../../shared/components/emission-value-with-suffix/EmissionValueWithSuffix';
import { DeclaredUnit } from '../../../../../../../../../../../shared/interfaces/IComponentDetails';
import {
  EmissionSymbol,
  IProcessDetails,
  MainRoute,
} from '../../../../../../../../../../../shared/interfaces/IFootprint';
import {
  MainRouteIconMap,
  MainRouteTranslationKeyMap,
} from '../../manufacturing-process-section/components/transport-process-form/helper/getMainRouteOptions';
import styles from '../CalculationDetails.module.css';

type TransportationDetailsProps = {
  process: IProcessDetails;
};

const TransportationDetails = ({ process }: TransportationDetailsProps): JSX.Element => {
  const { t } = useTranslation();

  return (
    <div className='flex flex-column w-full gap-5'>
      <div
        className={classNames(
          styles['transportation-process-type'],
          'py-1 px-2 -mb-3 w-fit font-medium',
        )}
      >
        {t('calculationDetailsPage.sections.transportation.transportationProcess')}
      </div>

      <div className='grid w-full grid-nogutter'>
        {/* Transportation / Route Name */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.transportation.transportationRouteName')}
          </span>
          <span className='text-lg'>{process?.route_name}</span>
        </div>

        {/* Main Route */}
        {process?.main_route && (
          <div className='col-6 flex flex-column gap-1 pl-2'>
            <span className='text-gray-700 font-medium'>
              {t('calculationDetailsPage.sections.transportation.mainRoute')}
            </span>
            <span className='text-lg flex flex-row gap-2 align-items-center'>
              {MainRouteIconMap[process.main_route as MainRoute]?.({})}

              {t(
                `footprintDetailsPage.sections.calculatePcfSection.transportProcessSection.inputs.mainRouteOptions.${
                  MainRouteTranslationKeyMap[process.main_route as MainRoute]
                }`,
              )}
            </span>
          </div>
        )}
      </div>

      <div className='flex flex-column gap-2'>
        {/* Starting Point */}
        <div className='flex flex-column gap-1 w-full'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.transportation.startingPoint')}
          </span>
          <span className='text-lg'>{process?.starting_point?.name}</span>
        </div>

        {/* Destination */}
        <div className='flex flex-column gap-1 w-full'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.transportation.destination')}
          </span>
          <span className='text-lg'>{process?.end_location?.name}</span>
        </div>
      </div>

      <div className='grid w-full grid-nogutter'>
        {/* Amount Per Piece */}
        <div className='col-6 flex flex-column gap-1 pr-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.transportation.amountPerPiece')}
          </span>
          <span className='text-lg'>{process?.product_weight} kg</span>
        </div>

        {/* Distance */}
        <div className='col-6 flex flex-column gap-1 pl-2'>
          <span className='text-gray-700 font-medium'>
            {t('calculationDetailsPage.sections.transportation.distance')}
          </span>
          <span className='text-lg flex flex-row gap-2 align-items-center'>
            {process?.route_calculation?.distance?.toFixed(1)} km
          </span>
        </div>
      </div>

      {/* Emissions */}
      <div className='flex flex-column gap-1 w-full'>
        <span className='text-gray-700 font-medium'>
          {t('calculationDetailsPage.sections.transportation.emissions')}
        </span>

        <EmissionValueWithSuffix
          emissionValue={process?.route_calculation?.total_emissions}
          declaredUnit={DeclaredUnit.piece}
          unitaryProductAmount={1}
          emissionSymbol={EmissionSymbol.KG_CO2E}
          abbreviateUnit={true}
          emissionValueDecimals={6}
          numericalValueTextClassName='text-lg'
          suffixTextClassName='text-lg'
        />
      </div>
    </div>
  );
};

export default TransportationDetails;
